// 健全度予測パラメータのプリセット定義

// 環境係数プリセット
export interface EnvPreset {
  key: string;
  label: string;
  value: number;
  description?: string;
}

export const ENV_PRESETS: EnvPreset[] = [
  {
    key: 'ENV_1',
    label: '極めて厳しい',
    value: 0.6,
    description: '高温・強腐食・塩素/酸性ガス・炉内近傍'
  },
  {
    key: 'ENV_2',
    label: 'やや厳しい',
    value: 0.85,
    description: '屋外・高湿/薬液ミスト・酸/アルカリ液'
  },
  {
    key: 'ENV_3',
    label: '標準',
    value: 1.0,
    description: '一般屋内/屋外'
  },
  {
    key: 'ENV_4',
    label: '良好',
    value: 1.2,
    description: '空調室・電気/計装室'
  },
  {
    key: 'ENV_5',
    label: '非常に良好',
    value: 1.5,
    description: 'クリーン・防食徹底'
  },
];

// 材質係数プリセット
export interface MatPreset {
  key: string;
  label: string;
  value: number;
  description?: string;
}

export const MAT_PRESETS: MatPreset[] = [
  {
    key: 'MAT_1',
    label: '一般材',
    value: 1.0,
    description: '炭素鋼/鋳鉄/SS400'
  },
  {
    key: 'MAT_2',
    label: '耐食（標準）',
    value: 1.3,
    description: 'SUS304, FRP, PVC/PP, ゴム/ライニング'
  },
  {
    key: 'MAT_3',
    label: '耐食（強）',
    value: 1.4,
    description: 'SUS316L'
  },
  {
    key: 'MAT_4',
    label: '高耐食合金',
    value: 1.7,
    description: '二相鋼, Ti, ハステロイ, Alloy625'
  },
];

// 設備タイププリセット
export interface EquipmentTypePreset {
  label: string;
  beta: number;
  suggestedEnv: string;
  suggestedMat: string;
}

export const EQUIPMENT_TYPE_PRESETS: EquipmentTypePreset[] = [
  {
    label: '標準（他が当てはまらない場合はこちら）',
    beta: 2.5,
    suggestedEnv: 'ENV_3',
    suggestedMat: 'MAT_1'
  },
  {
    label: 'タンク本体',
    beta: 2.5,
    suggestedEnv: 'ENV_3',
    suggestedMat: 'MAT_1'
  },
  {
    label: '圧力容器・オートクレーブ',
    beta: 3.0,
    suggestedEnv: 'ENV_3',
    suggestedMat: 'MAT_1'
  },
  {
    label: '配管・ダクト',
    beta: 2.5,
    suggestedEnv: 'ENV_3',
    suggestedMat: 'MAT_1'
  },
  {
    label: 'バルブ・弁類・ダンパ',
    beta: 3.0,
    suggestedEnv: 'ENV_3',
    suggestedMat: 'MAT_1'
  },
  {
    label: '回転機器（ポンプ・ブロワ・圧縮機・電動機）',
    beta: 3.0,
    suggestedEnv: 'ENV_3',
    suggestedMat: 'MAT_1'
  },
  {
    label: '搬送設備（コンベア・フィーダ・リフト）',
    beta: 3.0,
    suggestedEnv: 'ENV_3',
    suggestedMat: 'MAT_1'
  },
  {
    label: 'シックナー・沈降装置',
    beta: 2.0,
    suggestedEnv: 'ENV_3',
    suggestedMat: 'MAT_1'
  },
  {
    label: 'ろ過・フィルター設備',
    beta: 2.5,
    suggestedEnv: 'ENV_3',
    suggestedMat: 'MAT_1'
  },
  {
    label: '集塵・ガス処理',
    beta: 2.0,
    suggestedEnv: 'ENV_3',
    suggestedMat: 'MAT_1'
  },
  {
    label: '熱交換器',
    beta: 2.5,
    suggestedEnv: 'ENV_2',
    suggestedMat: 'MAT_2'
  },
  {
    label: '冷凍機・チラー',
    beta: 3.0,
    suggestedEnv: 'ENV_3',
    suggestedMat: 'MAT_1'
  },
  {
    label: '空調（パッケージ）',
    beta: 2.5,
    suggestedEnv: 'ENV_3',
    suggestedMat: 'MAT_1'
  },
  {
    label: 'ボイラー',
    beta: 3.0,
    suggestedEnv: 'ENV_2',
    suggestedMat: 'MAT_2'
  },
  {
    label: '特殊プロセス装置（破砕・乾燥・撹拌・炉など）',
    beta: 2.5,
    suggestedEnv: 'ENV_2',
    suggestedMat: 'MAT_1'
  },
  {
    label: 'クレーン・ホイスト',
    beta: 3.0,
    suggestedEnv: 'ENV_3',
    suggestedMat: 'MAT_1'
  },
  {
    label: '受変電設備（変圧器・配電盤）',
    beta: 2.5,
    suggestedEnv: 'ENV_4',
    suggestedMat: 'MAT_1'
  },
  {
    label: '計装制御盤・PLC',
    beta: 1.2,
    suggestedEnv: 'ENV_4',
    suggestedMat: 'MAT_1'
  },
  {
    label: 'センサー類',
    beta: 1.2,
    suggestedEnv: 'ENV_4',
    suggestedMat: 'MAT_1'
  },
  {
    label: '建築付帯構造（歩廊・デッキ・架台・屋根等）',
    beta: 2.5,
    suggestedEnv: 'ENV_3',
    suggestedMat: 'MAT_1'
  },
  {
    label: '電路・ケーブルトレイ',
    beta: 2.0,
    suggestedEnv: 'ENV_3',
    suggestedMat: 'MAT_1'
  },
  {
    label: '照明設備',
    beta: 1.8,
    suggestedEnv: 'ENV_3',
    suggestedMat: 'MAT_1'
  },
];

// ヘルパー関数
export const getEnvPresetByKey = (key: string): EnvPreset | undefined => {
  return ENV_PRESETS.find(preset => preset.key === key);
};

export const getMatPresetByKey = (key: string): MatPreset | undefined => {
  return MAT_PRESETS.find(preset => preset.key === key);
};

export const getEquipmentTypePreset = (label: string): EquipmentTypePreset | undefined => {
  return EQUIPMENT_TYPE_PRESETS.find(preset => preset.label === label);
};

export const getEnvPresetByValue = (value: number): EnvPreset | undefined => {
  return ENV_PRESETS.find(preset => Math.abs(preset.value - value) < 0.001);
};

export const getMatPresetByValue = (value: number): MatPreset | undefined => {
  return MAT_PRESETS.find(preset => Math.abs(preset.value - value) < 0.001);
};
